import { useState, useEffect, useRef, useMemo } from "react";
import RightSideDetails from "../global/rightSideDetails";
import swal from 'sweetalert2';
import Select from "react-select";
import * as helper from '../global/helper';
import {
    getDynamicPricingCalendarAll,
    postDynamicPricingCalendar,
    deleteDynamicPricingCalendar,
    getDynamicPricingTemplateSlots,
    postDynamicPricingTemplateSlots,
    getGreenFeeCalendarAll,
} from "../api/teeTimeAPI";

const DISCOUNT_TYPE_OPTIONS = [
    { label: 'Flat', value: 1 },
    { label: 'Percentage', value: 2 },
    { label: 'Capped Percentage', value: 3 },
];

const TEE_FILTER_OPTIONS = [
    { label: 'ALL', value: null },
    { label: '1', value: 1 },
    { label: '10', value: 10 },
];

const HOLE_OPTIONS = [
    { label: 'Both', value: null },
    { label: '9', value: 9 },
    { label: '18', value: 18 },
];

const discountTypeLabel = (value) => (DISCOUNT_TYPE_OPTIONS.find(o => o.value === value) || {}).label || '';
const holeLabel = (value) => (HOLE_OPTIONS.find(o => o.value === value) || {}).label || 'Both';

const DynamicPricingMaster = (props) => {

    const [isInit,            setIsInit]           = useState(true);
    const [rightSideDetails,  setRightSideDetails] = useState(false);

    const [allCalendars,      setAllCalendars]     = useState([]);
    const [calendar,          setCalendar]         = useState({ DiscountType: 1, Hole: null, Active: 1 });

    // TeeTimeMaster ("GreenFee Calendar") picker -->
    const [teeTimeMasters,    setTeeTimeMasters]   = useState([]);
    const [selectedCalenderID, setSelectedCalenderID] = useState(null);
    const calenderFilterRef = useRef(null);

    // Slot mapping drawer -->
    const [slotsDrawer,       setSlotsDrawer]      = useState(false);
    const [slotsCalendar,     setSlotsCalendar]    = useState(null);
    const [teeFilter,         setTeeFilter]        = useState(null);
    const [slots,             setSlots]            = useState([]);
    const [pendingChanges,    setPendingChanges]   = useState({});
    const [slotsLoading,      setSlotsLoading]     = useState(false);

    useEffect(() => {
        props.title("Dynamic Pricing", "dynamic-pricing-master");
        fetchTeeTimeMasters();
        fetchDynamicPricingCalendar();
    }, []);

    useEffect(() => {
        if (!isInit) {
            fetchDynamicPricingCalendar();
        }
    }, [selectedCalenderID]);

    useEffect(() => {
        if (rightSideDetails) {
            Config.Core.OpenDrawer();
            $('.date-input').datepicker({ format: 'dd-mm-yyyy', minDate: new Date() });
            $('#txtStartDate').on('change', ({ target }) => dataHandler('StartDate', moment(target.value, 'DD-MM-YYYY').format('YYYY-MM-DD')));
            $('#txtEndDate').on('change', ({ target }) => dataHandler('EndDate', moment(target.value, 'DD-MM-YYYY').format('YYYY-MM-DD')));
        } else {
            setCalendar({ DiscountType: 1, Hole: null, Active: 1 });
        }
    }, [rightSideDetails]);

    useEffect(() => {
        if (!isInit) {
            fillDataTable();
        }
    }, [allCalendars]);

    useEffect(() => {
        if (slotsDrawer) {
            Config.Core.OpenDrawer();
        }
    }, [slotsDrawer]);

    useEffect(() => {
        if (slotsDrawer && slotsCalendar) {
            fetchSlots(slotsCalendar.ID, teeFilter);
        }
    }, [slotsDrawer, slotsCalendar, teeFilter]);

    const dataHandler = (field, value) => setCalendar(d => ({ ...d, [field]: value }));

    // Once a calendar's own StartDate has arrived (today >= StartDate), its
    // StartDate/Hole/DiscountType/Discount/Capped are locked -- editable again
    // only before the calendar's StartDate arrives. Never applies to a brand
    // new calendar (no ID yet, no StartDate committed).
    const isCalendarLocked = useMemo(() => {
        return !!calendar.ID && !!calendar.StartDate && moment(calendar.StartDate).isSameOrBefore(moment(), 'day');
    }, [calendar.ID, calendar.StartDate]);

    const openAddDrawer = () => {
        if (!selectedCalenderID) {
            swal.fire({ icon: 'warning', titleText: 'Oops..', text: 'Please select a Calendar from the dropdown first.', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' })
                .then(() => calenderFilterRef.current && calenderFilterRef.current.focus());
            return;
        }
        setCalendar({ DiscountType: 1, Hole: null, Active: 1, CalenderID: selectedCalenderID });
        setRightSideDetails(true);
    }

    const fetchDynamicPricingCalendar = async () => {
        try {
            const calendars = await getDynamicPricingCalendarAll(undefined, undefined, selectedCalenderID);
            isInit ? setIsInit(false) : $('#tblDynamicPricingCalendar').KTDatatable().destroy();
            setAllCalendars(calendars);
        } catch (error) {
            swal.fire({ icon: 'error', titleText: error.message,  });
        }
    }

    const fetchTeeTimeMasters = async () => {
        try {
            const masters = await getGreenFeeCalendarAll();
            setTeeTimeMasters(masters || []);
        } catch (error) {
            swal.fire({ icon: 'error', titleText: error.message });
        }
    }

    const masterLabel = (m) => `${m.Name} (${moment(m.StartDate).format('DD-MMM-YYYY')} – ${moment(m.EndDate).format('DD-MMM-YYYY')})`;
    const calenderFilterOptions = [{ label: 'ALL Calendars', value: null }, ...teeTimeMasters.map(m => ({ label: masterLabel(m), value: m.ID }))];

    const postMaster = async () => {
        const btn = $('#btnSaveDynamicPricingCalendar');
        const form = $('#addDynamicPricingCalendarForm');

        form.validate({
            rules: {
                txtName: { required: true },
                txtStartDate: { required: true },
                txtEndDate: { required: true },
                txtDiscount: { required: true, number: true },
                txtCapped: { number: true },
            },
            messages: {
                txtName: { required: "Please enter name" },
                txtStartDate: { required: "Please select Start Date" },
                txtEndDate: { required: "Please select End Date" },
                txtDiscount: { required: "Please enter a discount value" },
            }
        });

        if (!form.valid()) return;

        if (moment(calendar.EndDate).isBefore(moment(calendar.StartDate))) {
            swal.fire({ icon: 'warning', titleText: 'Oops..', text: 'End Date must be on or after Start Date', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            return;
        }

        if (!calendar.CalenderID) {
            swal.fire({ icon: 'warning', titleText: 'Oops..', text: 'Please select a Calendar', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            return;
        }

        const master = teeTimeMasters.find(m => m.ID === calendar.CalenderID);
        if (master && (moment(calendar.StartDate).isBefore(moment(master.StartDate), 'day') || moment(calendar.EndDate).isAfter(moment(master.EndDate), 'day'))) {
            swal.fire({
                icon: 'warning',
                titleText: 'Oops..',
                text: `Start/End Date must fall within "${master.Name}"'s range (${moment(master.StartDate).format('DD-MMM-YYYY')} – ${moment(master.EndDate).format('DD-MMM-YYYY')})`,
                buttonsStyling: false,
                confirmButtonClass: 'btn btn-brand',
            });
            return;
        }

        if (calendar.DiscountType !== 1 && (calendar.Discount < 0 || calendar.Discount > 100)) {
            swal.fire({ icon: 'warning', titleText: 'Oops..', text: 'Percentage discount must be between 0 and 100', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            return;
        }

        if (calendar.DiscountType === 3 && (!calendar.Capped || calendar.Capped <= 0)) {
            swal.fire({ icon: 'warning', titleText: 'Oops..', text: 'Capped amount is required for Capped Percentage', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            return;
        }

        helper.StartProcessing(btn);
        try {
            const payload = { ...calendar };
            if (payload.DiscountType !== 3) payload.Capped = null;

            await postDynamicPricingCalendar(payload);
            helper.StopProcessing(btn);
            closeDrawer();
            await fetchDynamicPricingCalendar();

            if (calendar.ID) {
                swal.fire({
                    toast: true,
                    icon: 'success',
                    titleText: 'Dynamic Pricing Calendar updated successfully!',
                    position: 'bottom-end',
                    showConfirmButton: false,
                    timer: 1500,
                });
            } else {
                swal.fire({
                    icon: 'success',
                    titleText: 'Dynamic Pricing Calendar added successfully!',
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
        } catch (err) {
            helper.StopProcessing(btn);
            swal.fire({
                icon: 'error',
                titleText: 'Error!',
                text: err.message,
                buttonsStyling: false,
                confirmButtonClass: 'btn btn-brand',
            });
            return;
        }
    }

    const deleteHandler = (row) => {
        swal.fire({
            icon: 'warning',
            titleText: 'Delete this calendar?',
            text: `"${row.Name}" will be removed from this list and its dynamic discount will stop applying immediately.`,
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            buttonsStyling: false,
            confirmButtonClass: 'btn btn-brand',
            cancelButtonClass: 'btn btn-secondary margin-l10',
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            try {
                await deleteDynamicPricingCalendar(row.ID);
                fetchDynamicPricingCalendar();
                swal.fire({
                    toast: true,
                    icon: 'success',
                    titleText: 'Calendar deleted successfully!',
                    position: 'bottom-end',
                    showConfirmButton: false,
                    timer: 1500,
                });
            } catch (err) {
                swal.fire({ icon: 'error', titleText: 'Error!', text: err.message, buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            }
        });
    }

    function fillDataTable() {
        const dataTable = $('#tblDynamicPricingCalendar').KTDatatable({
            data: {
                saveState: false,
                source: allCalendars,
                pageSize: 20
            },
            layout: {
                scroll: true,
                height: $(window).height() - 250,
                footer: false,
            },
            sortable: true,
            pagination: true,
            search: { input: $('#generalSearch') },
            columns: [
                { field: 'Name', title: 'Name', width: 220 },
                {
                    field: 'CalenderID',
                    title: 'Calendar',
                    width: 160,
                    template: (row) => {
                        const m = teeTimeMasters.find(m => m.ID === row.CalenderID);
                        return m ? m.Name : '';
                    },
                },
                {
                    field: 'StartDate',
                    title: 'Start Date',
                    width: 120,
                    template: (row) => moment(row.StartDate).format('DD-MMM-YYYY'),
                },
                {
                    field: 'EndDate',
                    title: 'End Date', width: 120,
                    template: (row) => moment(row.EndDate).format('DD-MMM-YYYY'),
                },
                {
                    field: 'Hole',
                    title: 'Hole',
                    width: 80,
                    template: (row) => holeLabel(row.Hole),
                },
                {
                    field: 'DiscountType',
                    title: 'Discount',
                    width: 160,
                    template: (row) => row.DiscountType === 1
                        ? `Flat ₹${row.Discount}`
                        : row.DiscountType === 3
                            ? `${row.Discount}% (capped ₹${row.Capped})`
                            : `${row.Discount}%`,
                },
                {
                    field: 'Active',
                    title: 'Active',
                    width: 90,
                    template: (row) => row.Active ? '<span class="kt-badge kt-badge--success kt-badge--pill kt-badge--inline">Active</span>' : '<span class="kt-badge kt-badge--danger kt-badge--pill kt-badge--inline">Inactive</span>',
                },
                {
                    field: 'Actions',
                    title: 'Actions',
                    width: 130,
                    sortable: false,
                    template: (row) => `
                        <button type="button" class="btn btn-outline-info btn-elevate btn-circle btn-icon btn-xs margin-r5 btn-dp-slots" data-id="${row.ID}"><i class="la la-clock-o"></i></button>
                        <button type="button" class="btn btn-outline-info btn-elevate btn-circle btn-icon btn-xs margin-r5 btn-dp-edit" data-id="${row.ID}"><i class="flaticon2-edit"></i></button>
                        `,
                        // <button type="button" class="btn btn-outline-info btn-elevate btn-circle btn-icon btn-xs btn-dp-delete" data-id="${row.ID}"><i class="flaticon2-rubbish-bin-delete-button"></i></button>
                },
            ]
        });

        dataTable.on('click', '.btn-dp-edit', function (e) {
            e.stopPropagation();
            const id = $(this).data('id');
            const row = allCalendars.find(c => c.ID == id);
            if (row) {
                setCalendar(row);
                setRightSideDetails(true);
            }
        });

        dataTable.on('click', '.btn-dp-delete', function (e) {
            e.stopPropagation();
            const id = $(this).data('id');
            const row = allCalendars.find(c => c.ID == id);
            if (row) deleteHandler(row);
        });

        dataTable.on('click', '.btn-dp-slots', function (e) {
            e.stopPropagation();
            const id = $(this).data('id');
            const row = allCalendars.find(c => c.ID == id);
            if (row) openSlotsDrawer(row);
        });
    }

    const closeDrawer = () => {
        Config.Core.CloseDrawer();
        setTimeout(() => {
            setRightSideDetails(false);
        }, 200);
    }

    //#region Slots drawer -->

    const openSlotsDrawer = (row) => {
        setSlotsCalendar(row);
        setTeeFilter(null);
        setPendingChanges({});
        setSlotsDrawer(true);
    }

    const closeSlotsDrawer = () => {
        Config.Core.CloseDrawer();
        setTimeout(() => {
            setSlotsDrawer(false);
            setSlotsCalendar(null);
            setSlots([]);
            setPendingChanges({});
        }, 200);
    }

    const fetchSlots = async (calendarId, tee) => {
        setSlotsLoading(true);
        try {
            const response = await getDynamicPricingTemplateSlots(calendarId, tee);
            setSlots(response);
        } catch (error) {
            swal.fire({ icon: 'error', titleText: 'Error!', text: error.message, buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
        }
        setSlotsLoading(false);
    }

    const toggleSlot = (slotId, currentActive) => {
        const nextActive = currentActive ? 0 : 1;
        setPendingChanges(d => ({ ...d, [slotId]: nextActive }));
    }

    const saveSlots = async () => {
        const changedSlotIds = Object.keys(pendingChanges);
        if (changedSlotIds.length === 0) {
            closeSlotsDrawer();
            return;
        }

        const btn = $('#btnSaveDynamicPricingSlots');
        helper.StartProcessing(btn);
        try {
            const changedSlots = changedSlotIds.map(id => ({ SlotID: parseInt(id), Active: pendingChanges[id] }));
            await postDynamicPricingTemplateSlots(slotsCalendar.ID, changedSlots);
            helper.StopProcessing(btn);

            setPendingChanges({});
            await fetchSlots(slotsCalendar.ID, teeFilter);

            swal.fire({
                toast: true,
                icon: 'success',
                titleText: 'Slots updated successfully!',
                position: 'bottom-end',
                showConfirmButton: false,
                timer: 1500,
            });
        } catch (err) {
            helper.StopProcessing(btn);
            swal.fire({
                icon: 'error',
                titleText: 'Error!',
                text: err.message,
                buttonsStyling: false,
                confirmButtonClass: 'btn btn-brand',
            });
        }
    }

    const slotMinutes = (slot) => {
        const t = moment(slot, ['h:mm A', 'hh:mm A']);
        return t.isValid() ? (t.hours() * 60 + t.minutes()) : 0;
    };

    const displaySlots = slots
        .map(s => ({
            ...s,
            Active: (s.SlotID in pendingChanges) ? pendingChanges[s.SlotID] : s.Active,
        }))
        .sort((a, b) => (a.Tee - b.Tee) || (slotMinutes(a.Slot) - slotMinutes(b.Slot)));

    //#endregion

    return (
        <>
            <div className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor padding-tb0" id="kt_content">
                <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid margin-t20">
                    <div className="kt-portlet kt-portlet--mobile">
                        <div className="kt-portlet__head kt-portlet__head--lg padding-l0">
                            <div className="kt-portlet__head-label" style={{ flex: 1 }}>
                                <div className="row align-items-center margin-l10" style={{ width: '100%' }}>
                                    <div className="col-md-3 kt-margin-b-20-tablet-and-mobile margin-b0">
                                        <div className="kt-input-icon kt-input-icon--left">
                                            <input type="text" className="form-control clientSearch" placeholder="Search Calendar..." id="generalSearch" />
                                            <span className="kt-input-icon__icon kt-input-icon__icon--left"><span><i className="la la-search"></i></span></span>
                                        </div>
                                    </div>
                                    <div className="col-md-7 kt-margin-b-20-tablet-and-mobile margin-b0 d-flex justify-content-center">
                                        <div style={{ width: '100%', maxWidth: 340 }}>
                                            <Select
                                                ref={calenderFilterRef}
                                                id="ddlCalenderFilter"
                                                name="ddlCalenderFilter"
                                                openMenuOnFocus
                                                options={calenderFilterOptions}
                                                value={calenderFilterOptions.find(o => o.value === selectedCalenderID) || calenderFilterOptions[0]}
                                                onChange={(v) => setSelectedCalenderID(v.value)}
                                                placeholder="Select Calendar..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="kt-portlet__head-toolbar">
                                <div className="kt-portlet__head-wrapper">
                                    <div className="kt-portlet__head-actions">
                                        <button onClick={openAddDrawer} type="button" className="btn btn-brand btn-icon-sm text-white d-inline"><i className="la la-plus"></i> New Master </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="kt-portlet__body kt-portlet__body--fit">
                            <div id="tblDynamicPricingCalendar" className="kt-datatable table-striped clickable"></div>
                            {rightSideDetails && <RightSideDetails title="Dynamic Pricing Calendar" onCloseDrawer={() => closeDrawer()}>
                                <div className="kt-portlet">
                                    <div className="kt-portlet__body padding-t30 padding-b0">
                                        <form className="kt-form" id="addDynamicPricingCalendarForm">
                                            <div id="ControlHolder" className="kt-form kt-form--label-right padding-10" role="form">
                                                {isCalendarLocked && (
                                                    <div className="form-group row">
                                                        <div className="col-lg-12">
                                                            <div className="alert alert-warning" role="alert">
                                                                This calendar's Start Date has already arrived, so Start Date, Hole, Discount Type, Discount and Capped Amount can no longer be edited. Name, End Date, Comment and Active can still be changed.
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">Calendar</label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            disabled
                                                            value={(() => {
                                                                const m = teeTimeMasters.find(m => m.ID === calendar.CalenderID);
                                                                return m ? masterLabel(m) : '';
                                                            })()}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">Name <span className="red">*</span></label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <input
                                                            id="txtName"
                                                            name="txtName"
                                                            type="text"
                                                            className="form-control"
                                                            value={calendar.Name || ''}
                                                            onChange={({ target }) => dataHandler("Name", target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">Start Date <span className="red">*</span></label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <div className="input-group date">
                                                            <input
                                                                id="txtStartDate"
                                                                name="txtStartDate"
                                                                type="text"
                                                                className="form-control date-input"
                                                                readOnly={true}
                                                                disabled={isCalendarLocked}
                                                                placeholder="Select date"
                                                                value={calendar.StartDate ? moment(calendar.StartDate).format('DD-MM-YYYY') : ''}
                                                            />
                                                            <div className="input-group-append">
                                                                <span className="input-group-text">
                                                                    <i className="la la-calendar-check-o"></i>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">End Date <span className="red">*</span></label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <div className="input-group date">
                                                            <input
                                                                id="txtEndDate"
                                                                name="txtEndDate"
                                                                type="text"
                                                                className="form-control date-input"
                                                                readOnly={true}
                                                                placeholder="Select date"
                                                                value={calendar.EndDate ? moment(calendar.EndDate).format('DD-MM-YYYY') : ''}
                                                            />
                                                            <div className="input-group-append">
                                                                <span className="input-group-text">
                                                                    <i className="la la-calendar-check-o"></i>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">Hole</label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <Select
                                                            id="ddlHole"
                                                            name="ddlHole"
                                                            options={HOLE_OPTIONS}
                                                            value={{ label: holeLabel(calendar.Hole), value: calendar.Hole }}
                                                            onChange={(v) => dataHandler("Hole", v.value)}
                                                            isDisabled={isCalendarLocked}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">Discount Type <span className="red">*</span></label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <Select
                                                            id="ddlDiscountType"
                                                            name="ddlDiscountType"
                                                            options={DISCOUNT_TYPE_OPTIONS}
                                                            value={{ label: discountTypeLabel(calendar.DiscountType), value: calendar.DiscountType }}
                                                            onChange={(v) => dataHandler("DiscountType", v.value)}
                                                            isDisabled={isCalendarLocked}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">
                                                        {calendar.DiscountType === 1 ? 'Discount Amount' : 'Discount %'} <span className="red">*</span>
                                                    </label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <input
                                                            id="txtDiscount"
                                                            name="txtDiscount"
                                                            type="number"
                                                            className="form-control"
                                                            value={calendar.Discount ?? ''}
                                                            onChange={({ target }) => dataHandler("Discount", parseFloat(target.value))}
                                                            disabled={isCalendarLocked}
                                                        />
                                                    </div>
                                                </div>
                                                {calendar.DiscountType === 3 && (
                                                    <div className="form-group row">
                                                        <label className="col-form-label col-lg-3">Capped Amount <span className="red">*</span></label>
                                                        <div className="col-xl-6 col-lg-8">
                                                            <input
                                                                id="txtCapped"
                                                                name="txtCapped"
                                                                type="number"
                                                                className="form-control"
                                                                value={calendar.Capped ?? ''}
                                                                onChange={({ target }) => dataHandler("Capped", parseFloat(target.value))}
                                                                disabled={isCalendarLocked}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">Comment</label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <textarea
                                                            id="txtComment"
                                                            name="txtComment"
                                                            className="form-control"
                                                            value={calendar.Comment || ''}
                                                            onChange={({ target }) => dataHandler("Comment", target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-form-label col-lg-3">Active</label>
                                                    <div className="col-xl-6 col-lg-8">
                                                        <span className="kt-switch kt-switch--sm kt-switch--icon">
                                                            <label className="margin-b0 padding-t5">
                                                                <input
                                                                    id="chkActive"
                                                                    name="chkActive"
                                                                    type="checkbox"
                                                                    checked={calendar.Active === 1 || calendar.Active === undefined}
                                                                    onChange={(event) => dataHandler("Active", event.target.checked ? 1 : 0)}
                                                                />
                                                                <span></span>
                                                            </label>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="kt-portlet__foot">
                                                    <div className="kt-form__actions">
                                                        <div className="row">
                                                            <div className="col-lg-7 ml-lg-auto">
                                                                <button id="btnSaveDynamicPricingCalendar" type="button" className="btn btn-brand d-inline-block" onClick={postMaster}> Save </button>
                                                                <button id="btnCancel" type="button" className="btn btn-secondary margin-l10" onClick={closeDrawer}> Cancel </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </RightSideDetails>
                            }

                            {slotsDrawer && <RightSideDetails title={`Slots — ${slotsCalendar?.Name || ''}`} onCloseDrawer={() => closeSlotsDrawer()}>
                                <div className="kt-portlet">
                                    <div className="kt-portlet__body padding-t30 padding-b0">
                                        <div className="row margin-b20">
                                            <div className="col-lg-9"></div>
                                            <div className="col-lg-3">
                                                <Select
                                                    id="ddlTeeFilter"
                                                    name="ddlTeeFilter"
                                                    options={TEE_FILTER_OPTIONS}
                                                    value={TEE_FILTER_OPTIONS.find(o => o.value === teeFilter)}
                                                    onChange={(v) => setTeeFilter(v.value)}
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
                                            <table className="table table-bordered table-hover margin-b30">
                                                <thead className="light-grey-background" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                    <tr>
                                                        <th>Tee</th>
                                                        <th>Slot</th>
                                                        <th>Active</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {slotsLoading ? (
                                                        <tr><td colSpan={3}><div className="padding-10"><h5 className="text-center text-muted">Loading...</h5></div></td></tr>
                                                    ) : displaySlots.length > 0 ? (
                                                        displaySlots.map(s => (
                                                            <tr key={s.SlotID}>
                                                                <td>{s.Tee}</td>
                                                                <td>{s.Slot}</td>
                                                                <td className="padding-0" width="15%">
                                                                    <span className="kt-switch kt-switch--sm kt-switch--icon margin-lr10">
                                                                        <label className="margin-b0 padding-t5">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={!!s.Active}
                                                                                onChange={() => toggleSlot(s.SlotID, s.Active)}
                                                                            />
                                                                            <span></span>
                                                                        </label>
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr><td colSpan={3}><div className="padding-10"><h5 className="text-center text-muted">No Slots Available..!</h5></div></td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="kt-portlet__foot">
                                            <div className="kt-form__actions">
                                                <div className="row">
                                                    <div className="col-lg-7 ml-lg-auto">
                                                        <button id="btnSaveDynamicPricingSlots" type="button" className="btn btn-brand d-inline-block" onClick={saveSlots}> Save </button>
                                                        <button id="btnCancelSlots" type="button" className="btn btn-secondary margin-l10" onClick={closeSlotsDrawer}> Cancel </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </RightSideDetails>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default DynamicPricingMaster;
